import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import JSZip from "jszip";
import { useNavigate } from "react-router-dom";

import GetCookie from "../../../utils/GetCookie";
import authNavigate from "../../../globalComponents/AuthNav";

import ProcessingPopup from "../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../utils/popup/SuccessPopup";
import ConfirmationModal from "../../../utils/ConfirmationModal";

import DashboardHeader from "./DashboardHeader";
import ResultsList from "./ResultsList";
import HitsSummaryTab from "./HitsSummaryTab";

import LiteratureAbstracts from "./LiteratureAbstracts";
import LiteratureDiscoveryGraphComponent from "./LiteratureDiscoveryGraphComponent";
import GraphComponent from "../../copilotComponents/GraphComponent";
import DownloadDropdown from "./DownloadDropdown";
import MigrateDropdown from "./MigrateDropdown";
import GenieChatModal from "../../copilotComponents/Geniechatmodal";
import DocumentPreview from "../../../utils/DocumentPreview";
import { StatusBadgeXSm } from "../../../utils/StatusBadge";


const SEARCH_STEPS = [
    { upTo: 19, label: "Searching PubMed", icon: "bi-search", desc: "Querying PubMed for matching records" },
    { upTo: 59, label: "Fetching paper metadata", icon: "bi-cloud-download", desc: "Downloading titles, abstracts & authors" },
    { upTo: 69, label: "Generating PICO analysis", icon: "bi-robot", desc: "AI-extracting Population, Intervention, Comparison, Outcome" },
    { upTo: 79, label: "Building geographical map", icon: "bi-geo-alt", desc: "Mapping study origins by country" },
    { upTo: 89, label: "Generating timeline graph", icon: "bi-bar-chart-line", desc: "Charting publications over time" },
    { upTo: 100, label: "Saving results", icon: "bi-database-check", desc: "Storing papers and analytics to database" },
];

const GLOBAL_PLUGIN_OPTIONS = [
    { id: "PUBMED_CENTRAL", label: "PubMed Central" },
    { id: "UNPAYWALL", label: "Unpaywall" },
];

const DiscoveryProgressBar = ({ progress }) => {
    const pct = Math.min(Math.max(progress || 0, 0), 100);
    const activeIdx = SEARCH_STEPS.findIndex(s => pct <= s.upTo);
    const currentIdx = activeIdx === -1 ? SEARCH_STEPS.length - 1 : activeIdx;

    return (
        <div className="vh-55 d-flex flex-column justify-content-center p-4">

            {/* ── Header row ── */}
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                    <div className="discovery-step-icon-circle">
                        <i className={`bi ${SEARCH_STEPS[currentIdx].icon} ev-fs-14`} style={{ color: "#fff" }} />
                    </div>
                    <div>
                        <p className="ev-reg-p-dark ev-bold-med mb-0">
                            {SEARCH_STEPS[currentIdx].label}…
                        </p>
                        <p className="ev-small-p-dark text-muted mb-0">Discovery search in progress</p>
                    </div>
                </div>
                <span className="ev-bold-med discovery-pct-label">{pct}%</span>
            </div>

            {/* ── Progress bar ── */}
            <div className="discovery-progress-track">
                <div
                    className="discovery-progress-fill"
                    style={{
                        width: `${pct}%`,
                        animation: pct < 100 ? "gradient-move 3s linear infinite" : "none",
                    }}
                />
            </div>

            {/* ── Steps grid ── */}
            <div className="row g-2">
                {SEARCH_STEPS.map((s, i) => {
                    const done = pct >= s.upTo;
                    const active = i === currentIdx;
                    const state = done ? "done" : active ? "active" : "pending";
                    return (
                        <div key={i} className="col-4 col-md-2">
                            <div
                                className="ev-card-2 discovery-step-card h-100 d-flex flex-column align-items-start"
                                style={{
                                    border: active ? "1px solid var(--color9)" : "1px solid transparent",
                                    opacity: done || active ? 1 : 0.45,
                                    transition: "opacity 0.4s, border-color 0.4s",
                                }}
                            >
                                <div className={`discovery-step-icon-box ${state}`}>
                                    {done
                                        ? <i className="bi bi-check-lg ev-fs-13" style={{ color: "#fff" }} />
                                        : <i className={`bi ${s.icon} ev-fs-13`} style={{
                                            color: active ? "var(--color1)" : "var(--color17)",
                                        }} />
                                    }
                                </div>
                                <p className="ev-small-p-dark ev-bold-med mb-0">{s.label}</p>
                                <p className="ev-small-p-dark mb-0 mt-1">{s.desc}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


const LiteratureDiscoveryDashboard = ({ search, onBack, slrContext, onEdit, backLabel }) => {
    const navigate = useNavigate();

    const API_BASE_URL = "/pceapi/slr/discovery";
    const headers = useMemo(() => ({
        Authorization: GetCookie("access_token"),
    }), []);

    const [results, setResults] = useState([]);
    const [selectedResult, setSelectedResult] = useState(null);
    const [totalCount, setTotalCount] = useState(0);

    const [queryPrompt, setQueryPrompt] = useState("");
    const [finalQuery, setFinalQuery] = useState(search.query || search.search_query || "");
    const [limit, setLimit] = useState(null);

    // Backward-compat: pre-migration rows have no input_method saved — treat them
    // exactly like RESEARCH_QUESTION (today's only behavior) so old searches don't break.
    const inputMethod = search.input_method || "RESEARCH_QUESTION";
    const picosAnswers = search.form_answers || {};

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [activeTab, setActiveTab] = useState("abstracts");
    const [showDiscoveryAccess, setShowDiscoveryAccess] = useState(false);
    const [pdfPreview, setPdfPreview] = useState({ open: false, s3_path: null, title: null });

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [filteredResultIds, setFilteredResultIds] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    const [picoGraphData, setPicoGraphData] = useState(null);
    const [geoGraphData, setGeoGraphData] = useState(null);
    const [geoAuthorGraphData, setGeoAuthorGraphData] = useState(null);
    const [timeGraphData, setTimeGraphData] = useState(null);
    const [graphsLoading, setGraphsLoading] = useState(false);
    const [geoViewMode, setGeoViewMode] = useState("all");
    const [showGeoTooltip, setShowGeoTooltip] = useState(false);
    const [showQueryEditor, setShowQueryEditor] = useState(false);

    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedPhase, setSelectedPhase] = useState({
        id: "ABSTRACT",
        label: "Abstracts Screening Phase",
        shortLabel: "Abstracts Screening",
    });

    const [searchProcessingStatus, setSearchProcessingStatus] = useState(null);
    const [fullTextProcessingStatus, setFullTextProcessingStatus] = useState(null);

    const [abstractMigrationStatus, setAbstractMigrationStatus] = useState(null);
    const [fulltextMigrationStatus, setFulltextMigrationStatus] = useState(null);
    const [progressBar, setProgressBar] = useState(0);

    const [isProcessing, setIsProcessing] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isMigrating, setIsMigrating] = useState(false);
    const [isFindingFullText, setIsFindingFullText] = useState(false);
    const [downloadType, setDownloadType] = useState("abstract");
    const [isCopilotOpen, setIsCopilotOpen] = useState(false);
    const [isLinkingCopilot, setIsLinkingCopilot] = useState(false);
    const [genieContext, setGenieContext] = useState({ fabricId: null, threadId: null, docIds: [] });

    const [selectedGlobalPlugin, setSelectedGlobalPlugin] = useState(null);
    const [isPluginDropdownOpen, setIsPluginDropdownOpen] = useState(false);
    const [activePluginFilter, setActivePluginFilter] = useState("ALL");

    // ── Toolbar state (lifted from ResultsList) ──
    const [availabilityFilters, setAvailabilityFilters] = useState(new Set(["OPEN_ACCESS", "ORDER_PLACED", "PURCHASED", "UNPURCHASED", "UNKNOWN"]));
    const [genAiRightsFilters, setGenAiRightsFilters] = useState(new Set(["OPEN_ACCESS", "ORDER_PLACED", "PURCHASED", "UNPURCHASED", "UNKNOWN"]));
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const filterDropdownRef = useRef(null);

    // ── Sort state ──
    const [sortBy, setSortBy] = useState(null); // null | "RELEVANCE" | "NAME_ASC" | "NAME_DESC"
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
    const sortDropdownRef = useRef(null);

    const SORT_OPTIONS = [
        { id: null,         label: "Default" },
        { id: "RELEVANCE",  label: "By Relevance" },
        { id: "NAME_ASC",   label: "Title A → Z" },
        { id: "NAME_DESC",  label: "Title Z → A" },
    ];

    const getSortParams = (sortByState) => {
        if (sortByState === "RELEVANCE") return { sort_by: "RELEVANCE", sort_order: null };
        if (sortByState === "NAME_ASC")  return { sort_by: "NAME",      sort_order: "ASC" };
        if (sortByState === "NAME_DESC") return { sort_by: "NAME",      sort_order: "DESC" };
        return { sort_by: null, sort_order: null };
    };

    // ── Draft filter states (uncommitted until Apply is clicked) ──
    const [draftPluginFilter, setDraftPluginFilter] = useState("ALL");
    const [draftAvailabilityFilters, setDraftAvailabilityFilters] = useState(new Set(["OPEN_ACCESS", "ORDER_PLACED", "PURCHASED", "UNPURCHASED", "UNKNOWN"]));
    const [draftGenAiRightsFilters, setDraftGenAiRightsFilters] = useState(new Set(["OPEN_ACCESS", "ORDER_PLACED", "PURCHASED", "UNPURCHASED", "UNKNOWN"]));
    const [draftFromDate, setDraftFromDate] = useState("");
    const [draftToDate, setDraftToDate] = useState("");

    const ALL_ACCESS_IDS = ["OPEN_ACCESS", "ORDER_PLACED", "PURCHASED", "UNPURCHASED", "UNKNOWN"];
    const ACCESS_STATUS_OPTIONS = [
        { id: "OPEN_ACCESS", label: "Open Access" },
        { id: "ORDER_PLACED", label: "Order Placed" },
        { id: "PURCHASED", label: "Purchased" },
        { id: "UNPURCHASED", label: "Unpurchased" },
        { id: "UNKNOWN", label: "Unknown" },
    ];
    const PLUGIN_FILTER_OPTIONS = [
        { id: "ALL", label: "All" },
        { id: "PUBMED_CENTRAL", label: "PubMed Central" },
        { id: "UNPAYWALL", label: "Unpaywall" },
    ];

    const toggleFilter = (setter, id) => {
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAllFilters = (setter, currentSet) => {
        setter(currentSet.size === ALL_ACCESS_IDS.length ? new Set() : new Set(ALL_ACCESS_IDS));
    };

    // Sync draft states from actual states when dropdown opens
    const handleFilterDropdownOpen = () => {
        if (!filterDropdownOpen) {
            setDraftPluginFilter(activePluginFilter);
            setDraftAvailabilityFilters(new Set(availabilityFilters));
            setDraftGenAiRightsFilters(new Set(genAiRightsFilters));
            setDraftFromDate(fromDate);
            setDraftToDate(toDate);
        }
        setFilterDropdownOpen(prev => !prev);
    };

    const handleApplyFilters = () => {
        setActivePluginFilter(draftPluginFilter);
        setAvailabilityFilters(new Set(draftAvailabilityFilters));
        setGenAiRightsFilters(new Set(draftGenAiRightsFilters));
        setFromDate(draftFromDate);
        setToDate(draftToDate);
        const from = extractYear(draftFromDate);
        const to = extractYear(draftToDate);
        const pluginParam = draftPluginFilter !== "ALL" ? draftPluginFilter : null;
        fetchDiscoveryDetails(from, to, null, null, pluginParam, getSortParams(sortBy));
        setFilterDropdownOpen(false);
    };

    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
        setSortDropdownOpen(false);
        const from = extractYear(fromDate);
        const to = extractYear(toDate);
        const pluginParam = activePluginFilter !== "ALL" ? activePluginFilter : null;
        fetchDiscoveryDetails(from, to, null, null, pluginParam, getSortParams(newSortBy));
    };

    const filteredSelectedCount = useMemo(() =>
        results.filter(r => selectedIds.has(r.id) &&
            (activePluginFilter === "ALL" || r.plugin === activePluginFilter) &&
            availabilityFilters.has(r.full_text_status || "UNKNOWN") &&
            genAiRightsFilters.has(r.genai_status || "UNKNOWN")
        ).length,
        [results, selectedIds, activePluginFilter, availabilityFilters, genAiRightsFilters]
    );

    const visibleResultsCount = useMemo(() =>
        results.filter(r =>
            (activePluginFilter === "ALL" || r.plugin === activePluginFilter) &&
            availabilityFilters.has(r.full_text_status || "UNKNOWN") &&
            genAiRightsFilters.has(r.genai_status || "UNKNOWN") &&
            (!showSelectedOnly || selectedIds.has(r.id))
        ).length,
        [results, activePluginFilter, availabilityFilters, genAiRightsFilters, showSelectedOnly, selectedIds]
    );

    const isInitialSelection = useRef(true);
    const pluginDropdownRef = useRef(null);
    const queryEditorRef = useRef(null);
    const sseControllerRef = useRef(null);

    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const [galaxyOrderModal, setGalaxyOrderModal] = useState({ open: false, paperId: null, price: null, orderType: "PDF" });

    const isSearchRunning = searchProcessingStatus === "RUNNING";
    const isFullTextRunning = fullTextProcessingStatus === "RUNNING";

    const selectAll = useMemo(
        () => filteredResultIds.length > 0 && filteredResultIds.every(id => selectedIds.has(id)),
        [filteredResultIds, selectedIds]
    );

    const activeSelectedIds = useMemo(() => {
        const filteredSet = new Set(filteredResultIds);
        return [...selectedIds].filter(id => filteredSet.has(id));
    }, [selectedIds, filteredResultIds]);

    const selectedPmids = useMemo(() =>
        results
            .filter((r) => activeSelectedIds.includes(r.id))
            .map((r) => r.pmid)
            .filter(Boolean),
        [results, activeSelectedIds]
    );

    const extractYear = (dateStr) => {
        if (!dateStr) return null;
        if (/^\d{4}$/.test(dateStr)) return dateStr;
        return new Date(dateStr).getFullYear().toString();
    };

    const handleClearFilters = () => {
        setFromDate("");
        setToDate("");
        setLimit(null);
        setActivePluginFilter("ALL");
        setAvailabilityFilters(new Set(ALL_ACCESS_IDS));
        setGenAiRightsFilters(new Set(ALL_ACCESS_IDS));
        setShowSelectedOnly(false);
        // Reset draft states too
        setDraftPluginFilter("ALL");
        setDraftAvailabilityFilters(new Set(ALL_ACCESS_IDS));
        setDraftGenAiRightsFilters(new Set(ALL_ACCESS_IDS));
        setDraftFromDate("");
        setDraftToDate("");
        setSortBy(null);
        fetchDiscoveryDetails(null, null, null, null, null, null);
    };

    const startSSEStream = (discoveryId) => {
    if (sseControllerRef.current) {
        sseControllerRef.current.abort();
    }

    const controller = new AbortController();
    sseControllerRef.current = controller;

    fetch(`${API_BASE_URL}/status-stream`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: GetCookie("access_token"),
        },
        body: JSON.stringify({ discovery_id: discoveryId }),
        signal: controller.signal,
    })
    .then((response) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        const read = () => {
            reader.read().then(({ done, value }) => {
                if (done) return;

                const text = decoder.decode(value, { stream: true });
                const lines = text.split("\n");

                lines.forEach((line) => {
                    if (line.startsWith("data:")) {
                        try {
                            const data = JSON.parse(line.replace("data:", "").trim());
                            console.log("SSE EVENT:", data);
                            if (data.type === "progress") {
                                setProgressBar(data.progress);
                            }
                            if (data.type === "status") {
                                setSearchProcessingStatus(data.status);
                                if (data.status === "COMPLETED" || data.status === "FAILED") {
                                    controller.abort();
                                    sseControllerRef.current = null;
                                    fetchDiscoveryDetails();
                                    fetchGraphs();
                                }
                            }
                        } catch (e) {
                            // skip malformed line
                        }
                    }
                });
                read();
            }).catch(() => {
                sseControllerRef.current = null;
            });
        };
        read();
    })
    .catch(() => {
        sseControllerRef.current = null;
    });
};

    const fetchGraphs = async () => {
        if (!search?.id) return;

        setGraphsLoading(true);

        try {
            const res = await axios.post(
                `${API_BASE_URL}/graphs`,
                { discovery_id: search.id },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                const data = res.data.data;
                setPicoGraphData(data.pico_graph ? { figure_dict: data.pico_graph } : null);
                setGeoGraphData(data.geo_graph ? { figure_dict: data.geo_graph } : null);
                setGeoAuthorGraphData(data.geo_author_graph ? { figure_dict: data.geo_author_graph } : null);
                setTimeGraphData(data.time_graph ? { figure_dict: data.time_graph } : null);
            }
        } catch (err) {
            console.error("Error fetching graphs:", err);
        } finally {
            setGraphsLoading(false);
        }
    };

    const fetchDiscoveryDetails = async (from = null, to = null, fullTextStatus = null, genaiStatus = null, pluginFilter = null, sortByParam = null) => {
        console.log('[fetchDiscoveryDetails] called — plugin:', pluginFilter, 'sortBy:', sortByParam);
        if (!isSearchRunning && !isFullTextRunning) {
            setIsProcessing(true);
            setProcessingMessage("Loading search results...");
        }

        try {
            const res = await axios.post(
                `${API_BASE_URL}/view-details`,
                {
                    discovery_id: search.id,
                    sort_from: from,
                    sort_to: to,
                    full_text_status: fullTextStatus,
                    genai_status: genaiStatus,
                    plugin: pluginFilter,
                    sort_by: sortByParam?.sort_by ?? null,
                    sort_order: sortByParam?.sort_order ?? null,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                const payload = res.data.data;

                setSearchProcessingStatus(payload.processing_status);
                setFullTextProcessingStatus(payload.fulltext_status);
                setAbstractMigrationStatus(payload.abstract_migration_status);
                setFulltextMigrationStatus(payload.fulltext_migration_status);
                setProgressBar(payload.estimated_completion_at || 0);
                setResults(payload.literatures || []);
                setTotalCount(payload.total_results || 0);
                setFinalQuery(payload.search_query || "");

                if (payload.literatures?.length) {
                    if (isInitialSelection.current) {
                        if (slrContext?.included_pmids?.length) {
                            const pmidSet = new Set(slrContext.included_pmids);
                            const matchedIds = new Set(
                                payload.literatures
                                    .filter((r) => pmidSet.has(r.pmid))
                                    .map((r) => r.id)
                            );
                            setSelectedIds(matchedIds);
                        } else {
                            setSelectedIds(new Set(payload.literatures.map((r) => r.id)));
                        }
                        isInitialSelection.current = false;
                    } else {
                        // Preserve user's selection — only prune IDs no longer in results
                        const validIds = new Set(payload.literatures.map((r) => r.id));
                        setSelectedIds(prev => new Set([...prev].filter(id => validIds.has(id))));
                    }

                    if (!selectedResult) {
                        setSelectedResult(payload.literatures[0]);
                        setActiveTab("abstracts");
                    }
                } else {
                    setSelectedResult(null);
                    setSelectedIds(new Set());
                }

                if (payload.discovery_access?.length) {
                    setSelectedUserIds(payload.discovery_access.map((u) => u.user_id));
                } else {
                    setSelectedUserIds([]);
                }
                return payload.processing_status;
            } else {
                setErrorMessage(res.data?.message || "Failed to load discovery details");
            }
        } catch (err) {
            setErrorMessage(err.message || "Error fetching discovery details");
        } finally {
            setIsProcessing(false);
        }
    };

    const saveAccess = async () => {
        setIsProcessing(true);
        setProcessingMessage("Saving access...");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/save-user-access`,
                {
                    discovery_id: search.id,
                    user_ids: selectedUserIds,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                setSuccessMessage("Access saved successfully");
                setShowDiscoveryAccess(false);
            } else {
                setErrorMessage(res.data?.message || "Failed to save access");
            }
        } catch (err) {
            setErrorMessage(err.message || "Error saving access");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUserSelection = (userId) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

    const handleFilteredResultsChange = useCallback((filtered) => {
        setFilteredResultIds(filtered.map(r => r.id));
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
                setFilterDropdownOpen(false);
            }
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
                setSortDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);



    const handleSelectAll = () => {
        if (selectAll) {
            const filteredSet = new Set(filteredResultIds);
            setSelectedIds(prev => new Set([...prev].filter(id => !filteredSet.has(id))));
        } else {
            setSelectedIds(prev => new Set([...prev, ...filteredResultIds]));
        }
    };

    const handleSelectOne = (id) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedIds(newSet);
    };

    const handleSelectResult = (result) => {
        setSelectedResult(result);
    };

    const handleViewArticle = (result) => {
        setSelectedResult(result);
        setActiveTab("abstract_view");
    };

    const handleRunSearch = async () => {
        setIsProcessing(true);
        setProcessingMessage("Running literature search...");

        // Clear stale graph data immediately so old charts don't show during new search
        setPicoGraphData(null);
        setGeoGraphData(null);
        setGeoAuthorGraphData(null);
        setTimeGraphData(null);

        try {
            const res = await axios.post(
                `${API_BASE_URL}/run`,
                {
                    discovery_id: search.id,
                    search_query: finalQuery,
                    search_limit: limit || null,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                setSuccessMessage(res.data.message || "Discovery search started");
                setSearchProcessingStatus("RUNNING");
                isInitialSelection.current = true;
                setActivePluginFilter("ALL");
                await fetchDiscoveryDetails();
                await fetchGraphs();
                startSSEStream(search.id);
            } else {
                setErrorMessage(res.data?.message || "Failed to run search");
            }
        } catch (err) {
            setErrorMessage(err.response?.data?.message || "Error running search");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRefresh = async () => {
        const from = extractYear(fromDate);
        const to = extractYear(toDate);

        setIsRefreshing(true);
        setProcessingMessage("Refreshing results...");
        setErrorMessage("");

        try {
            await Promise.all([
                fetchDiscoveryDetails(from, to, null, null, activePluginFilter !== "ALL" ? activePluginFilter : null, getSortParams(sortBy)),
                fetchGraphs(),
            ]);
        } finally {
            setIsRefreshing(false);
            setProcessingMessage("");
        }
    };

    const handleGenerateQuery = async () => {
        if (!queryPrompt.trim() || isGenerating || isProcessing) return;

        setIsGenerating(true);
        setProcessingMessage("Generating query...");
        setErrorMessage("");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/generate-query`,
                { natural_language_query: queryPrompt.trim() },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                setFinalQuery(res.data?.data?.pubmed_query ?? "");
                setSuccessMessage("Query generated successfully");
            } else {
                setErrorMessage(res.data?.message || "Failed to generate query");
            }
        } catch (err) {
            setErrorMessage(err?.response?.data?.message || "Error generating query");
        } finally {
            setIsGenerating(false);
            setProcessingMessage("");
        }
    };

    const handleMigrate = async ({ project, phase }) => {
        if (!project?.id) {
            setErrorMessage("Please select an SLR Project to migrate to.");
            return;
        }

        if (activeSelectedIds.length === 0) {
            setErrorMessage("Please select at least one paper to migrate.");
            return;
        }

        // Order by current table display order (respects active sort — e.g. Relevance),
        // not Set insertion order which drifts from click/select history.
        const selectedResultIds = results
            .filter((r) => activeSelectedIds.includes(r.id))
            .map((r) => r.id);
        const migrationTypeMap = {
            abstract: "abstract",
            ABSTRACT: "abstract",
            full_text: "full_text",
            FULL_TEXT: "full_text",
        };
        const migrationType = migrationTypeMap[phase?.id] || "abstract";

        setIsMigrating(true);
        setProcessingMessage(`Migrating ${selectedResultIds.length} papers to ${project.name}...`);

        try {
            const res = await axios.post(
                `${API_BASE_URL}/migrate`,
                {
                    fabric_id: project.id,
                    discovery_result_ids: selectedResultIds,
                    migration_type: migrationType,
                    search_id: search.id,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                setSuccessMessage(
                    res.data.message || `Successfully migrated ${selectedResultIds.length} papers to ${project.name}`
                );
            } else {
                setErrorMessage(res.data?.message || "Migration failed.");
            }
        } catch (err) {
            console.error("Migration error:", err);
            setErrorMessage(err.response?.data?.message || "Failed to migrate papers.");
        } finally {
            setIsMigrating(false);
            setProcessingMessage("");
        }
    };

    

    const handleSearchFullArticle = async () => {
        if (!selectedGlobalPlugin) {
            setErrorMessage("Please select a plugin first.");
            return;
        }

        if (activeSelectedIds.length === 0) {
            setErrorMessage("Please select at least one paper.");
            return;
        }

        setIsFindingFullText(true);
        setProcessingMessage("Searching full articles...");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/full-text`,
                {
                    discovery_id: search.id,
                    plugin: selectedGlobalPlugin,
                    paper_ids: activeSelectedIds,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                setSuccessMessage(res.data.message || "Full article search started");
                setFullTextProcessingStatus("RUNNING");
                await fetchDiscoveryDetails();
            } else {
                setErrorMessage(res.data?.message || "Failed to search full articles.");
            }
        } catch (err) {
            console.error("Full article search error:", err);
            setErrorMessage("An error occurred while searching for full articles.");
        } finally {
            setIsFindingFullText(false);
            setProcessingMessage("");
        }
    };

    const handleRowSearchPlugin = async (resultId, pluginId) => {
        setIsFindingFullText(true);
        setProcessingMessage("Searching with plugin...");

        try {
            const res = await axios.post(
                `${API_BASE_URL}/full-text/single`,
                {
                    discovery_id: search.id,
                    paper_id: resultId,
                    plugin: pluginId,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                setSuccessMessage(res.data.message || "Plugin search completed");
                await fetchDiscoveryDetails();
            } else {
                setErrorMessage(res.data?.message || "Plugin search failed.");
            }
        } catch (err) {
            console.error("Plugin search error:", err);
            setErrorMessage("An error occurred during plugin search.");
        } finally {
            setIsFindingFullText(false);
            setProcessingMessage("");
        }
    };

    // genieContextRef stays in sync for use inside closures
    // Genie is offered on the abstract tabs only. A chat left open while
    // switching to Hits Summary / graphs sat over their pagination.
    useEffect(() => {
        if (activeTab !== "abstracts" && activeTab !== "abstract_view") setIsCopilotOpen(false);
    }, [activeTab]);

    const genieContextRef = useRef(genieContext);
    useEffect(() => { genieContextRef.current = genieContext; }, [genieContext]);

    const handleAskCopilot = async () => {
        // 1. Already populated this session → open directly
        if (genieContext?.fabricId && genieContext?.docIds?.length > 0) {
            setIsCopilotOpen(true);
            return;
        }

        // 2. Discovery already linked (fabric_id came from list response) → open directly
        if (search?.fabric_id) {
            setGenieContext({
                fabricId: search.fabric_id,
                threadId: search.thread_id ?? null,
                docIds: search.doc_id != null ? [search.doc_id] : [],
            });
            setIsCopilotOpen(true);
            return;
        }

        // 3. Not yet linked — first-time setup only
        if (isLinkingCopilot) return;

        setIsLinkingCopilot(true);
        setProcessingMessage("Setting up Genie...");

        // skip_csv_refresh=true when the discovery already has a fabric_id in the
        // list response — it was linked before, we only need the existing context back.
        const alreadyLinked = Boolean(search?.fabric_id);

        try {
            const res = await axios.post(
                `${API_BASE_URL}/link-copilot`,
                { discovery_id: search.id, skip_csv_refresh: alreadyLinked },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") {
                authNavigate('/login', 'iam', navigate);
                return;
            }

            if (res.data?.status === "success") {
                const payload = res.data.data || res.data;
                setGenieContext({
                    fabricId: payload.fabric_id ?? null,
                    threadId: payload.thread_id ?? null,
                    docIds: payload.doc_id != null ? [payload.doc_id] : [],
                });
                setIsCopilotOpen(true);
            } else {
                setErrorMessage(res.data?.message || "Failed to link Discovery to Genie.");
            }
        } catch (err) {
            console.error("Link genie error:", err);
            setErrorMessage(err.response?.data?.message || "Failed to connect to Genie.");
        } finally {
            setIsLinkingCopilot(false);
            setProcessingMessage("");
        }
    };

    const handleGalaxyOrder = (paperId, orderType = "PDF") => {
        const paper = results.find((r) => r.id === paperId);
        const price = orderType === "GENAI" ? paper?.genai_price : paper?.delivery_price;
        setGalaxyOrderModal({ open: true, paperId, price, orderType });
    };

    const handleGalaxyOrderConfirm = async () => {
        const { paperId, orderType } = galaxyOrderModal;
        setGalaxyOrderModal({ open: false, paperId: null, price: null, orderType: "PDF" });
        setIsProcessing(true);
        setProcessingMessage(orderType === "GENAI" ? "Placing GenAI rights order..." : "Placing Galaxy order...");
        try {
            const res = await axios.post(
                `${API_BASE_URL}/galaxy-order`,
                { discovery_id: search.id, paper_id: paperId, order_type: orderType },
                { headers }
            );
            if (res.data?.status === "success") {
                setResults((prev) =>
                    prev.map((r) =>
                        r.id === paperId
                            ? orderType === "GENAI"
                                ? { ...r, genai_status: "ORDER_PLACED" }
                                : { ...r, full_text_status: "ORDER_PLACED" }
                            : r
                    )
                );
                setSuccessMessage(orderType === "GENAI" ? "GenAI rights order placed successfully." : "Order placed successfully.");
            } else {
                setErrorMessage(res.data?.message || "Failed to place order");
            }
        } catch (err) {
            setErrorMessage(err.response?.data?.message || "Error placing Galaxy order");
        } finally {
            setIsProcessing(false);
            setProcessingMessage("");
        }
    };

    useEffect(() => {
    if (!search?.id) return;

    const loadDashboardData = async () => {
        const [status] = await Promise.all([
            fetchDiscoveryDetails(),
            fetchGraphs(),
        ]);
        if (status === "RUNNING") {
            startSSEStream(search.id);
        }
    };

    loadDashboardData();
}, [search?.id]);

    useEffect(() => {
    return () => {
        if (sseControllerRef.current) {
            sseControllerRef.current.abort();
            sseControllerRef.current = null;
        }
    };
}, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pluginDropdownRef.current && !pluginDropdownRef.current.contains(e.target)) {
                setIsPluginDropdownOpen(false);
            }
            if (queryEditorRef.current && !queryEditorRef.current.contains(e.target)) {
                setShowQueryEditor(false);
            }
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
                setFilterDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="mx-1 pb-4 position-relative">
            <DashboardHeader
                searchProcessingStatus={searchProcessingStatus}
                isProcessing={isProcessing}
                showDiscoveryAccess={showDiscoveryAccess}
                setShowDiscoveryAccess={setShowDiscoveryAccess}
                selectedUserIds={selectedUserIds}
                onUserSelection={handleUserSelection}
                onSaveAccess={saveAccess}
                ownerEmail={search.owner}
                apiBaseUrl={API_BASE_URL}
                onRunSearch={handleRunSearch}
                onRefresh={handleRefresh}
                onBack={onBack}
                backLabel={backLabel}
                search={search}
                selectedPmids={selectedPmids}
                headers={headers}
                setErrorMessage={setErrorMessage}
                setSuccessMessage={setSuccessMessage}
            />

            <div className="mb-2">
                <div className="d-flex justify-content-between align-items-end mb-2">
                    <label className="ev-small-p-dark ev-bold-reg mb-0">
                        {inputMethod === "MANUAL" ? (
                            <>Manual PICOS <i className="bi bi-pencil-fill text-success"></i></>
                        ) : inputMethod === "PASTE_QUERY" ? (
                            <>Custom query <i className="gradient-font6 bi bi-hash"></i></>
                        ) : (
                            <>Generated by AI <i className="gradient-font6 bi bi-stars"></i></>
                        )}
                    </label>

                    <div className="d-flex justify-content-end align-items-center gap-2">

                        <input
                            type="number"
                            min={1}
                            className="formField5 ev-btn-small w-30 ev-bold-med ev-radius-xsmall"
                            placeholder="Limit"
                            value={limit ?? ""}
                            onChange={(e) => {
                                const val = e.target.value;
                                setLimit(val ? Number(val) : null);
                            }}
                        />
                        <button
                            type="button"
                            className="ev-btn-yellow-fill-round ev-btn-small ev-radius-xsmall ev-bold-med text-nowrap"
                            onClick={() => onEdit?.(search)}
                        >
                            <i className="bi bi-pencil me-1"></i> Go to Searches
                        </button>
                    </div>
                </div>

                {/* ── Query Strip (collapsible) ── */}
                <div className="ev-card-2 ev-radius-small ev-padding-small mb-2 position-relative" ref={queryEditorRef}>
                    <div className="d-flex align-items-center gap-2">
                        {inputMethod === "MANUAL" ? (
                            <div className="d-flex align-items-center gap-2 flex-grow-1 flex-wrap">
                                {[
                                    { key: "P", value: picosAnswers.POPULATION },
                                    { key: "I", value: picosAnswers.INTERVENTION },
                                    { key: "C", value: picosAnswers.COMPARATOR },
                                    { key: "O", value: picosAnswers.OUTCOMES },
                                ].filter((chip) => chip.value).map((chip) => (
                                    <span key={chip.key} className="ev-message-card4 ev-fs-10 text-nowrap">
                                        <strong>{chip.key}:</strong> {chip.value}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <span className="ev-small-p-dark text-truncate flex-grow-1" title={finalQuery}>
                                {finalQuery || "No query set"}
                            </span>
                        )}
                        {search.step_count && (
                            <span className="ev-message-card4 ev-fs-10 text-nowrap">{search.step_count} steps · ✓ Valid</span>
                        )}
                        <button type="button" className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall text-nowrap" onClick={() => setShowQueryEditor((prev) => !prev)}>
                            {showQueryEditor ? (<><i className="bi bi-chevron-up me-1"></i> Collapse</>) : (<><i className="bi bi-chevron-down me-1"></i> Expand</>)}
                        </button>
                    </div>

                    {showQueryEditor && (
                        <div
                            className="ev-card-4 ev-radius-small position-absolute start-0 end-0 mt-1 p-3 shadow-lg query-editor-overlay"
                            style={{ top: "100%", zIndex: 20, maxHeight: "65vh", overflowY: "auto" }}
                        >
                            {inputMethod === "PASTE_QUERY" ? (
                                <div className="row g-2">
                                    <div className="col-6">
                                        <div className="ev-card-6 p-1 ev-radius-xsmall">
                                            <input type="text" className="formField3 bg-transparent w-100" placeholder="Auto-generate PubMed search query" value={queryPrompt} onChange={(e) => setQueryPrompt(e.target.value)} />
                                            <div className="text-end">
                                                <button type="button" className="ev-btn-gradient-fill-round ev-radius-xsmall ev-bold-reg ev-btn-small" onClick={handleGenerateQuery} disabled={!queryPrompt.trim() || isProcessing || isGenerating}>
                                                    {isGenerating ? (<><i className="bi bi-hourglass-split me-1"></i> Generating...</>) : (<><i className="bi bi-play-fill"></i> Generate</>)}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <textarea className="formField3 vh-9 ev-message-card2 w-100 pt-2" value={finalQuery} onChange={(e) => setFinalQuery(e.target.value)} />
                                    </div>
                                </div>
                            ) : (
                                /* RESEARCH_QUESTION / MANUAL — read-only summary of what the search was built from.
                                   Actual editing happens by reopening the Create Search screen (see wireframe note). */
                                <div>
                                    {inputMethod === "RESEARCH_QUESTION" && (
                                        <div className="mb-2">
                                            <p className="ev-small-p-dark ev-bold-med mb-1">Research question</p>
                                            <div className="ev-card-2 ev-radius-xsmall p-2 ev-small-p-dark">
                                                {search.research_question || "—"}
                                            </div>
                                        </div>
                                    )}
                                    <div className="row g-2 mb-2">
                                        {[
                                            { label: "Population", value: picosAnswers.POPULATION },
                                            { label: "Intervention", value: picosAnswers.INTERVENTION },
                                            { label: "Comparator", value: picosAnswers.COMPARATOR },
                                            { label: "Outcomes", value: picosAnswers.OUTCOMES },
                                            { label: "Study type", value: picosAnswers.STUDY_DESIGN },
                                        ].map((field) => (
                                            <div className="col" key={field.label}>
                                                <p className="ev-small-p-dark ev-bold-med mb-1">{field.label}</p>
                                                <div className="ev-card-2 ev-radius-xsmall p-2 ev-small-p-dark text-truncate" title={field.value || ""}>
                                                    {field.value || "—"}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <p className="ev-small-p-dark ev-bold-med mb-0">Generated PubMed query</p>
                                            {search.step_count && (
                                                <span className="ev-message-card4 ev-fs-10 text-nowrap">{search.step_count} steps · ✓ Valid</span>
                                            )}
                                        </div>
                                        <div className="ev-card-2 ev-radius-xsmall p-2 ev-small-p-dark" style={{ whiteSpace: "pre-wrap" }}>
                                            {finalQuery || "No query set"}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="ev-card-4 mx-1 px-3 py-2">
                    {/* Tabs row with icons and badges */}
                    <div className="d-flex justify-content-between align-items-center gap-1 mb-2">
                        <div className="ev-message-card6 ev-radius-xsmall p-1 d-flex gap-2">
                            {[
                                { key: "abstracts", label: "Article Details", count: totalCount },
                                { key: "abstract_view", label: "Abstracts" },
                                { key: "pico", label: "PICO" },
                                { key: "hits", label: "Hits Summary", count: search.hits_count },
                                { key: "geo", label: "Geographical" },
                                { key: "time", label: "Time Graph" },
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    className={activeTab === tab.key
                                        ? "ev-btn-blue-fill-round ev-btn-small ev-radius-xsmall ev-bold-med"
                                        : "ev-btn-light-fill ev-btn-small ev-radius-xsmall ev-bold-med"
                                    }
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="d-flex align-items-center gap-4 flex-shrink-0">
                            <MigrateDropdown
                                selectedProject={selectedProject}
                                onSelectProject={setSelectedProject}
                                phases={[{ id: "ABSTRACT", label: "Abstracts Screening Phase", shortLabel: "Abstracts Screening" }]}
                                selectedPhase={selectedPhase}
                                onSelectPhase={setSelectedPhase}
                                onMigrate={handleMigrate}
                                disabled={activeSelectedIds.length === 0 || isMigrating}
                                isLoading={isMigrating}
                                abstractMigrationStatus={abstractMigrationStatus}
                                fulltextMigrationStatus={fulltextMigrationStatus}
                            />
                        </div>{/* end Migrate + Fetch group */}
                    </div>

                    {/* ── Shared toolbar — always visible for abstracts & abstract_view ── */}
                    {(activeTab === "abstracts" || activeTab === "abstract_view") && (
                        <div className="d-flex align-items-center justify-content-between mb-2 ev-message-card3 ev-radius-xsmall p-2">
                            <div className="d-flex align-items-center gap-2">
                                <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                                <span className="ev-reg-p-dark ev-bold-med">{selectAll ? "Unselect all" : "Select all"}</span>
                                <span className="ev-reg-p-blue ev-bold-med ms-2">{filteredSelectedCount} of {visibleResultsCount} selected</span>
                                <button type="button"
                                    className={`ev-btn-small ev-bold-med ev-radius-xlarge ms-2 d-flex align-items-center gap-1 ${showSelectedOnly ? "ev-btn-blue-fill-round" : "ev-btn-skyblue-fill-round ev-small-p-blue"}`}
                                    onClick={() => setShowSelectedOnly(prev => !prev)}
                                    disabled={!showSelectedOnly && filteredSelectedCount === 0}>
                                    Show Selected
                                    {showSelectedOnly && <i className="bi bi-x ev-fs-12"></i>}
                                </button>
                            </div>

                            {/* Sort dropdown + Filters dropdown */}
                            <div className="d-flex align-items-center gap-2">
                                <div className="position-relative" ref={sortDropdownRef}>
                                    <button
                                        type="button"
                                        className={`ev-btn-small ev-bold-med ev-radius-xsmall d-flex align-items-center gap-1 ${sortBy ? "ev-btn-blue-fill-round" : "ev-btn-light-fill ev-reg-p-blue"}`}
                                        onClick={() => setSortDropdownOpen(prev => !prev)}
                                    >
                                        <i className={`bi bi-sort-down ev-fs-11 ${sortBy ? "" : "ev-reg-p-blue"}`}></i>
                                        <span className={sortBy ? "" : "ev-reg-p-blue"}>
                                            {SORT_OPTIONS.find(o => o.id === sortBy)?.label || "Sort"}
                                        </span>
                                        <i className={`bi ${sortDropdownOpen ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11`}></i>
                                    </button>
                                    {sortDropdownOpen && (
                                        <div
                                            className="ev-card-8 ev-radius-xsmall position-absolute top-100 end-0 mt-1 p-2 z-3 shadow"
                                            style={{ minWidth: "160px" }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {SORT_OPTIONS.map((opt) => (
                                                <button
                                                    key={String(opt.id)}
                                                    type="button"
                                                    className={`w-100 text-start ev-radius-xsmall ev-btn-small mb-1 ${sortBy === opt.id ? "ev-btn-blue-fill-round" : "ev-btn-light-fill ev-reg-p-blue"}`}
                                                    onClick={() => handleSortChange(opt.id)}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="position-relative" ref={filterDropdownRef}>
                                    <button
                                        type="button"
                                        className={`ev-btn-small ev-bold-med ev-radius-xsmall d-flex align-items-center gap-1 ${(fromDate || toDate) ? "ev-btn-blue-fill-round" : "ev-btn-light-fill ev-reg-p-blue"}`}
                                        onClick={handleFilterDropdownOpen}
                                    >
                                        <i className={`bi bi-funnel ev-fs-11 ${(fromDate || toDate) ? "" : "ev-reg-p-blue"}`}></i>
                                        <span className={(fromDate || toDate) ? "" : "ev-reg-p-blue"}>
                                            {(fromDate || toDate)
                                                ? `${fromDate || "—"} → ${toDate || "—"}`
                                                : "Filter"}
                                        </span>
                                        <i className={`bi ${filterDropdownOpen ? "bi-chevron-up" : "bi-chevron-down"} ev-fs-11`}></i>
                                    </button>

                                    {filterDropdownOpen && (
                                        <div
                                            className="ev-card-8 ev-radius-xsmall position-absolute top-100 end-0 mt-1 p-3 z-3 shadow"
                                            style={{ minWidth: "220px" }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <p className="ev-small-p-dark ev-bold-med mb-2">Publication Year</p>

                                            <div className="row g-2 mb-3">
                                                <div className="col-6">
                                                    <p className="ev-small-p-dark mb-1">From</p>
                                                    <input
                                                        type="number"
                                                        className="discovery-section-header ev-radius-xsmall w-100"
                                                        placeholder="e.g. 2015"
                                                        min="1900"
                                                        max={new Date().getFullYear()}
                                                        value={draftFromDate}
                                                        onChange={(e) => setDraftFromDate(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-6">
                                                    <p className="ev-small-p-dark mb-1">To</p>
                                                    <input
                                                        type="number"
                                                        className="discovery-section-header ev-radius-xsmall w-100"
                                                        placeholder="e.g. 2024"
                                                        min={draftFromDate || "1900"}
                                                        max={new Date().getFullYear()}
                                                        value={draftToDate}
                                                        onChange={(e) => setDraftToDate(e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <div className="d-flex justify-content-end gap-2">
                                                <button
                                                    type="button"
                                                    className="ev-btn-light-fill ev-btn-small ev-radius-xsmall ev-reg-p-blue"
                                                    onClick={() => {
                                                        setFilterDropdownOpen(false);
                                                        handleClearFilters();
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    type="button"
                                                    className="ev-btn-blue-fill-round ev-btn-small ev-radius-xsmall"
                                                    onClick={handleApplyFilters}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>{/* end sort+filter group */}
                        </div>
                    )}

                    <div className="row">
                        {(activeTab === "abstracts" || activeTab === "abstract_view") && isSearchRunning ? (
                            <div className="col-12">
                                <DiscoveryProgressBar progress={progressBar} />
                            </div>
                        ) : (activeTab === "abstracts" || activeTab === "abstract_view") && results.length === 0 ? (
                            <div className="col-12 d-flex flex-column align-items-center justify-content-center vh-55 py-5">
                                {(searchProcessingStatus === null || searchProcessingStatus === "NOT_BEGUN") ? (
                                    <>
                                        <p className="ev-reg-p-dark ev-bold-med mb-1">No Search Run Yet</p>
                                        <p className="ev-small-p-dark text-muted text-center mb-3">Run your search to discover literature results.</p>
                                        <button
                                            type="button"
                                            className="ev-btn-gradient-fill-round ev-btn-med ev-bold-med d-inline-flex align-items-center gap-2"
                                            onClick={handleRunSearch}
                                        >
                                            <i className="bi bi-play-fill" />
                                            Run Search
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <p className="ev-reg-p-dark ev-bold-med mb-1">No Literature Results Found</p>
                                        <p className="ev-small-p-dark text-muted text-center mb-0">Try adjusting your search filters or run a new search to find literature.</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            <>
                                {/* Hidden ResultsList — always mounted to keep filter callbacks alive */}
                                {(activeTab === "abstracts" || activeTab === "abstract_view") && (
                                    <div className="d-none">
                                        <ResultsList
                                            results={results}
                                            selectedResult={selectedResult}
                                            selectedIds={selectedIds}
                                            selectAll={selectAll}
                                            onSelectAll={handleSelectAll}
                                            onSelectOne={handleSelectOne}
                                            onSelectResult={handleSelectResult}
                                            onViewArticle={handleViewArticle}
                                            onSearchPlugin={handleRowSearchPlugin}
                                            onViewPdf={(s3_path, title) => setPdfPreview({ open: true, s3_path, title })}
                                            onGalaxyOrder={handleGalaxyOrder}
                                            onFilteredResultsChange={handleFilteredResultsChange}
                                            pluginFilter={activePluginFilter}
                                            onPluginFilterChange={() => {}}
                                            fullWidth
                                            availabilityFilters={availabilityFilters}
                                            genAiRightsFilters={genAiRightsFilters}
                                            showSelectedOnly={showSelectedOnly}
                                            setShowSelectedOnly={setShowSelectedOnly}
                                            searchProcessingStatus={searchProcessingStatus}
                                            onRunSearch={handleRunSearch}
                                        />
                                    </div>
                                )}

                                {/* Filtered-empty state — full width, no ResultsList header */}
                                {(activeTab === "abstracts" || activeTab === "abstract_view") && filteredResultIds.length === 0 && (
                                    <div className="col-12 d-flex flex-column align-items-center justify-content-center vh-55 py-5">
                                        <i className="bi bi-journal-x ev-fs-50 text-muted mb-3 d-block text-center"></i>
                                        <p className="ev-reg-p-dark ev-bold-med mb-1">No Literature Results Found</p>
                                        <p className="ev-small-p-dark text-muted text-center mb-0">Try adjusting your search filters or run a new search to find literature.</p>
                                    </div>
                                )}

                                {/* Normal ResultsList — only when filtered results exist */}
                                {activeTab === "abstracts" && filteredResultIds.length > 0 && (
                                    <ResultsList
                                        results={results}
                                        selectedResult={selectedResult}
                                        selectedIds={selectedIds}
                                        selectAll={selectAll}
                                        onSelectAll={handleSelectAll}
                                        onSelectOne={handleSelectOne}
                                        onSelectResult={handleSelectResult}
                                        onViewArticle={handleViewArticle}
                                        onSearchPlugin={handleRowSearchPlugin}
                                        onViewPdf={(s3_path, title) => setPdfPreview({ open: true, s3_path, title })}
                                        onGalaxyOrder={handleGalaxyOrder}
                                        onFilteredResultsChange={handleFilteredResultsChange}
                                        pluginFilter={activePluginFilter}
                                        onPluginFilterChange={() => {}}
                                        fullWidth
                                        availabilityFilters={availabilityFilters}
                                        genAiRightsFilters={genAiRightsFilters}
                                        showSelectedOnly={showSelectedOnly}
                                        setShowSelectedOnly={setShowSelectedOnly}
                                        searchProcessingStatus={searchProcessingStatus}
                                        onRunSearch={handleRunSearch}
                                    />
                                )}

                                {activeTab === "abstract_view" && filteredResultIds.length > 0 && (
                                    <>
                                        <ResultsList
                                            results={results}
                                            selectedResult={selectedResult}
                                            selectedIds={selectedIds}
                                            selectAll={selectAll}
                                            onSelectAll={handleSelectAll}
                                            onSelectOne={handleSelectOne}
                                            onSelectResult={handleSelectResult}
                                            onSearchPlugin={handleRowSearchPlugin}
                                            onViewPdf={(s3_path, title) => setPdfPreview({ open: true, s3_path, title })}
                                            onGalaxyOrder={handleGalaxyOrder}
                                            onFilteredResultsChange={handleFilteredResultsChange}
                                            pluginFilter={activePluginFilter}
                                            onPluginFilterChange={() => {}}
                                            availabilityFilters={availabilityFilters}
                                            genAiRightsFilters={genAiRightsFilters}
                                            showSelectedOnly={showSelectedOnly}
                                            setShowSelectedOnly={setShowSelectedOnly}
                                            searchProcessingStatus={searchProcessingStatus}
                                            onRunSearch={handleRunSearch}
                                        />
                                        <div className="col-7 ps-1">
                                            <LiteratureAbstracts literature={selectedResult} />
                                        </div>
                                    </>
                                )}

                                <div className={(activeTab === "abstracts" || activeTab === "abstract_view") ? "d-none" : "col-12"}>
                                    {activeTab === "hits" && (
                                        <HitsSummaryTab
                                            discoveryId={search.id}
                                            apiBaseUrl={API_BASE_URL}
                                            headers={headers}
                                        />
                                    )}

                                    {activeTab === "pico" && (
                                        graphsLoading ? (
                                            <div className="ev-card-2 p-4 text-center vh-55">
                                                <p className="ev-small-p-dark">Loading PICO graph...</p>
                                            </div>
                                        ) : picoGraphData ? (
                                            <div>
                                                <p className="text-center ev-bold-med ev-reg-p-dark mb-2">
                                                    PEOS Term Frequency Distribution
                                                </p>
                                                <div className="scrollable-element">
                                                    <LiteratureDiscoveryGraphComponent
                                                        graphData={picoGraphData}
                                                        isLoading={false}
                                                        contentId={1}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="ev-card-2 p-4 text-center">
                                                <p className="ev-small-p-dark">
                                                    PICO graph not available. Run search first.
                                                </p>
                                            </div>
                                        )
                                    )}

                                    {activeTab === "geo" && (
                                        graphsLoading ? (
                                            <div className="ev-card-2 p-4 text-center">
                                                <p className="ev-small-p-dark">Loading geographical distribution...</p>
                                            </div>
                                        ) : (geoGraphData || geoAuthorGraphData) ? (
                                            <div>
                                                <div className="d-flex align-items-center justify-content-between mb-0">
                                                    <div className="d-flex align-items-center gap-2 mt-1 ms-2">
                                                        <p className="ev-reg-p-dark ev-bold-med mb-0">
                                                            {geoViewMode === "all"
                                                                ? "Geographical Distribution of Studies Based on Author Affiliations"
                                                                : "Geographical Distribution of Single-Country Studies Based on Author Affiliations"}
                                                        </p>
                                                        <span
                                                            style={{ position: "relative", display: "inline-flex" }}
                                                            onMouseEnter={() => setShowGeoTooltip(true)}
                                                            onMouseLeave={() => setShowGeoTooltip(false)}
                                                        >
                                                            <i className="bi bi-info-circle ev-fs-13" style={{ color: "var(--color9)", cursor: "pointer" }} />
                                                            {showGeoTooltip && (
                                                                <div
                                                                    className="ev-card-2 p-2"
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: "100%",
                                                                        left: "50%",
                                                                        transform: "translateX(-50%)",
                                                                        marginTop: "6px",
                                                                        width: "300px",
                                                                        zIndex: 10,
                                                                    }}
                                                                >
                                                                    <p className="ev-small-p-dark mb-0">
                                                                        {geoViewMode === "all"
                                                                            ? "Note: In the default view, studies with authors from multiple countries are counted once for each respective country. Use the filter to view studies affiliated with only one country."
                                                                            : "This view shows only studies where all authors are affiliated with a single country."}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </span>
                                                    </div>
                                                    <select
                                                        className="formField2 ev-btn-small ev-radius-xsmall ev-bold-med me-2"
                                                        value={geoViewMode}
                                                        onChange={(e) => setGeoViewMode(e.target.value)}
                                                        style={{ width: "auto", minWidth: "220px" }}
                                                    >
                                                        <option value="all">All Papers</option>
                                                        <option value="single_country">Single-Country Authors Only</option>
                                                    </select>
                                                </div>

                                                {geoViewMode === "all" && geoGraphData && (
                                                    <div style={{ marginTop: "-5px" }}>
                                                        <GraphComponent
                                                            graphData={geoGraphData}
                                                            isLoading={false}
                                                            contentId="2"
                                                        />
                                                    </div>
                                                )}

                                                {geoViewMode === "all" && !geoGraphData && (
                                                    <div className="ev-card-2 p-4 text-center">
                                                        <p className="ev-small-p-dark">
                                                            Geographical data not available. Run search first.
                                                        </p>
                                                    </div>
                                                )}

                                                {geoViewMode === "single_country" && geoAuthorGraphData && (
                                                    <div style={{ marginTop: "-5px" }}>
                                                        <GraphComponent
                                                            graphData={geoAuthorGraphData}
                                                            isLoading={false}
                                                            contentId="2b"
                                                        />
                                                    </div>
                                                )}

                                                {geoViewMode === "single_country" && !geoAuthorGraphData && (
                                                    <div className="ev-card-2 p-4 text-center">
                                                        <p className="ev-small-p-dark">
                                                            Single-country author data not available. Run search first.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="ev-card-2 p-4 text-center">
                                                <p className="ev-small-p-dark">
                                                    Geographical data not available. Run search first.
                                                </p>
                                            </div>
                                        )
                                    )}

                                    {activeTab === "time" && (
                                        graphsLoading ? (
                                            <div className="ev-card-2 p-4 text-center vh-56">
                                                <p className="ev-small-p-dark">Loading time graph...</p>
                                            </div>
                                        ) : timeGraphData ? (
                                            <GraphComponent
                                                graphData={timeGraphData}
                                                isLoading={false}
                                                contentId="3"
                                            />
                                        ) : (
                                            <div className="ev-card-2 p-4 text-center vh-56">
                                                <p className="ev-small-p-dark">
                                                    Time graph not available. Run search first.
                                                </p>
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* <DocumentPreview
                    isOpen={pdfPreview.open}
                    onClose={() => setPdfPreview({ open: false, s3_path: null, title: null })}
                    s3_path={pdfPreview.s3_path}
                    documentName={pdfPreview.title}
                    bucket_type="output"
                    showOpenInTab={true}
                /> */}

                {/* <ConfirmationModal
                    show={galaxyOrderModal.open}
                    onClose={() => setGalaxyOrderModal({ open: false, paperId: null, price: null, orderType: "PDF" })}
                    onConfirm={handleGalaxyOrderConfirm}
                    title={galaxyOrderModal.orderType === "GENAI" ? "Purchase GenAI Rights" : "Place Galaxy Order"}
                    message={`This will place a paid order for ${galaxyOrderModal.orderType === "GENAI" ? "GenAI rights" : "the full text PDF"} at $${galaxyOrderModal.price?.toFixed(2)}. Do you want to proceed?`}
                    confirmButtonText="Place Order"
                    confirmButtonClass="btn-primary"
                /> */}

                {(isProcessing || isRefreshing || isDownloading || isMigrating || isGenerating || isLinkingCopilot || isFindingFullText) && (
                    <ProcessingPopup message={processingMessage || "Processing..."} />
                )}

                {errorMessage && (
                    <ErrorPopup
                        message={errorMessage}
                        onClose={() => setErrorMessage("")}
                    />
                )}

                {successMessage && (
                    <SuccessPopup
                        message={successMessage}
                        onClose={() => setSuccessMessage("")}
                    />
                )}

                {/* Floating Ask Genie button — hidden on non-abstract tabs to avoid overlapping pagination */}
                {!isCopilotOpen && (activeTab === "abstracts" || activeTab === "abstract_view") && (
                    <button
                        type="button"
                        className="ev-btn-gradient-fill-round ev-btn-reg ev-bold-med position-fixed bottom-0 end-0 mb-5 me-4 z-3"
                        onClick={handleAskCopilot}
                        disabled={isLinkingCopilot || searchProcessingStatus !== "COMPLETED" || totalCount === 0}
                    >
                        {isLinkingCopilot
                            ? (<><span className="spinner-border spinner-border-sm me-1"></span> Setting up...</>)
                            : (<><i className="bi bi-stars me-1"></i> Ask Genie</>)}
                    </button>
                )}

                {isCopilotOpen && (
                    <GenieChatModal
                        isOpen={isCopilotOpen}
                        onClose={() => setIsCopilotOpen(false)}
                        discoveryId={search.id}
                        fabricId={genieContext.fabricId}
                        threadId={genieContext.threadId}
                        docIds={genieContext.docIds}
                        paperCount={totalCount}
                    />
                )}
            </div>
        </div>
    );
};

export default LiteratureDiscoveryDashboard;